import { prepareCategoryOptions } from '@openx/components-targeting/Targeting/Category/utils';
import type { CategoryOptionsMap } from '@openx/types/targeting/category';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type OptionsByPathRefetchResult, useOptionsByPathQuery } from '../../../hooks/useOptionsByPathQuery';

export const useCategoriesOptionsFetch = (): {
  categories: CategoryOptionsMap;
  isCategoriesLoading: boolean;
  refetchCategories: () => Promise<OptionsByPathRefetchResult>;
} => {
  const { t } = useTranslation();
  const {
    data: { optionsByPath = [] } = {},
    loading,
    refetch,
  } = useOptionsByPathQuery({
    variables: {
      path: 'deal.package.targeting.domain.categories',
    },
  });

  const list = useMemo(() => {
    if (!optionsByPath) {
      return [];
    }

    return optionsByPath.map(option =>
      option
        ? {
            id: option.id,
            name: option.name,
            ...option.extra,
          }
        : []
    );
  }, [optionsByPath]);

  const mapById = useMemo(() => {
    return list
      .map(option => ({ [option.id]: option }))
      .reduce((acc, curr) => {
        return Object.assign(acc, curr);
      }, {});
  }, [list]);

  const preparedCategories = useMemo(() => (mapById ? prepareCategoryOptions(mapById, t('(All)')) : {}), [mapById, t]);

  return {
    categories: preparedCategories,
    isCategoriesLoading: loading,
    refetchCategories: refetch,
  };
};
